import React, { useRef, KeyboardEvent, memo } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane, faStop } from '@fortawesome/free-solid-svg-icons'
import PulseLoader from 'react-spinners/PulseLoader'
import TextareaAutosize from 'react-textarea-autosize'
import { Tooltip } from 'react-tooltip'
import theme from '~/design'

const MAXIMUM_MESSAGE_CHARACTERS = 2000

const InputContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  cursor: text;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0px 5px 20px 3px rgba(0, 0, 0, 0.075);
  transition: all 250ms ease-in-out;
  padding: 5px 5px 45px;

  &:focus-within {
    box-shadow: 0px 5px 30px 3px rgba(0, 0, 0, 0.15);
  }
`

const MessageTextarea = styled(TextareaAutosize)`
  flex: 1;
  padding: 15px;
  border: none;
  font-family: inherit;
  font-size: 16px;
  min-height: 60px;
  max-height: 30vh;
  resize: none;
  width: 100%;
  border-radius: 5px;

  &:focus {
    outline: none;
  }
`

const SendButton = styled.button`
  padding: 5px;
  width: 45px;
  height: 45px;
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  right: 10px;
  bottom: 10px;
  font-size: 18px;
  opacity: 1;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.7;
    transform: scale(1.1);
  }

  &:disabled {
    background: ${theme.colors.mediumGrey};
    cursor: not-allowed;
  }
`

const CharacterCounter = styled.div<{ $isOverLimit: boolean }>`
  position: absolute;
  left: 18px;
  bottom: 14px;
  font-size: 11px;
  font-family: 'Figtree', Arial, sans-serif;
  color: ${props => (props.$isOverLimit ? theme.colors.danger : theme.colors.mediumGrey)};
  user-select: none;
`

interface MessageInputProps {
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  isLoading?: boolean
  input?: string
  setInput?: React.Dispatch<React.SetStateAction<string>>
  placeholder?: string
  onStop?: () => void
  isThinking?: boolean
  inputRef?: React.RefObject<HTMLTextAreaElement>
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSubmit,
  input,
  setInput,
  onStop,
  isThinking = false,
  isLoading = false,
  placeholder = 'Digite para começar um novo chat...',
  inputRef,
}) => {
  const formRef = useRef<HTMLFormElement>(null)
  const characterCount = input?.length ?? 0
  const isOverLimit = characterCount > MAXIMUM_MESSAGE_CHARACTERS

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (input?.trim() && !isLoading && !isThinking && !isOverLimit) {
      onSubmit?.(e)
      setInput?.('')
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      formRef.current?.requestSubmit()
    }
  }

  return (
    <InputContainer onClick={() => inputRef?.current?.focus()}>
      <form ref={formRef} onSubmit={handleSubmit}>
        <MessageTextarea
          ref={inputRef}
          value={input}
          maxLength={MAXIMUM_MESSAGE_CHARACTERS}
          onChange={event => {
            const nextValue = event.target.value
            const value =
              nextValue.length > MAXIMUM_MESSAGE_CHARACTERS
                ? nextValue.slice(0, MAXIMUM_MESSAGE_CHARACTERS)
                : nextValue

            setInput?.(value)
          }}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          disabled={isLoading}
          minRows={2}
          maxRows={12}
          name='input-message'
        />
        <SendButton
          className='message-llm-send-button'
          type='submit'
          onClick={e => {
            if (isThinking) {
              e.preventDefault()
              onStop?.()
            }
          }}
          disabled={!isThinking && (!input?.trim() || isLoading || isOverLimit)}
        >
          {isLoading && !isThinking ? (
            <PulseLoader size={5.5} color='#fff' speedMultiplier={0.7} />
          ) : isThinking ? (
            <FontAwesomeIcon icon={faStop} color='#fff' />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} color='#fff' />
          )}
        </SendButton>
        {!input?.trim() && !isThinking && !isLoading && (
          <Tooltip
            anchorSelect='.message-llm-send-button'
            place='top'
            content='Mensagem está vazia'
            />
        )}
        <CharacterCounter $isOverLimit={isOverLimit}>
          {characterCount}/{MAXIMUM_MESSAGE_CHARACTERS}
        </CharacterCounter>
        {isThinking && (
          <Tooltip
            anchorSelect='.message-llm-send-button'
            place='top'
            content='Interromper a resposta'
          />
        )}
        {isOverLimit && !isThinking && (
          <Tooltip
            anchorSelect='.message-llm-send-button'
            place='top'
            content={`Limite de ${MAXIMUM_MESSAGE_CHARACTERS} caracteres`}
          />
        )}
      </form>
    </InputContainer>
  )
}

export default memo(MessageInput)
